import { CoreFetch, ProviderUser, UserApi } from '../../api';
import { WorkerConfigurationApiMode } from './worker-configuration.api';

export interface AssignableUser {
    bankUserId: string;
    email?: string;
    first?: string;
    last?: string;
}

export function formatAssignableUserLabel(user: AssignableUser): string {
    const name = `${user.first || ''} ${user.last || ''}`.trim();
    return name || user.email || user.bankUserId;
}

export function resolveAssignableUserInput(input: string, users: AssignableUser[]): string | undefined {
    const trimmed = input.trim();
    if (!trimmed) {
        return undefined;
    }

    const lower = trimmed.toLowerCase();

    const byId = users.find(user => user.bankUserId === trimmed);
    if (byId) {
        return byId.bankUserId;
    }

    const byEmail = users.find(user => user.email?.toLowerCase() === lower);
    if (byEmail) {
        return byEmail.bankUserId;
    }

    const byExactName = users.filter(user => {
        const fullName = `${user.first || ''} ${user.last || ''}`.trim().toLowerCase();
        return fullName === lower;
    });
    if (byExactName.length === 1) {
        return byExactName[0].bankUserId;
    }

    const partialMatches = users.filter(user => {
        const fullName = `${user.first || ''} ${user.last || ''}`.trim().toLowerCase();
        return (
            fullName.includes(lower) ||
            user.email?.toLowerCase().includes(lower) === true ||
            user.bankUserId.toLowerCase().includes(lower)
        );
    });
    if (partialMatches.length === 1) {
        return partialMatches[0].bankUserId;
    }

    return undefined;
}

export function toAssignableUser(user: ProviderUser): AssignableUser | null {
    const bankUserId = user.personalAccountId?.trim();
    if (!bankUserId) {
        return null;
    }

    return {
        bankUserId,
        email: user.email,
        first: user.first,
        last: user.last,
    };
}

export async function fetchAssignableUsers(_apiMode: WorkerConfigurationApiMode): Promise<AssignableUser[]> {
    const api = CoreFetch(UserApi);
    const users = await api.listProviderUsers({ filterActive: 'EQ:1' });

    return users.map(toAssignableUser).filter((user): user is AssignableUser => user !== null);
}
